import { useCallback } from "react";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import { useActiveFlow } from "./ActiveFlowContext";
import { evaluateSkipIf } from "./flowConditions";
import type { FlowConditionContext } from "./flowConditions";
import type { ScreenConfigs, DocumentItemConfig } from "./flowConfig.types";
import { DEFAULT_FLOW_CONFIG } from "./FlowConfigContext";
import type { OnboardingStackParamList } from "./types";

// Steps whose screen component fires the /submit-kyc batch as its terminal
// action. Kept in sync with the callers of submitKycFromContext.
const SUBMIT_KYC_STEP_IDS = new Set<string>([
  "DocumentOwnership",
  "YourDocuments",
  "AddressDetails",
]);

const FALLBACK_DOCUMENT_CONFIGS: DocumentItemConfig[] = (
  DEFAULT_FLOW_CONFIG.db_types[0].steps.find((s) => s.id === "Documents")
    ?.config as { documents: DocumentItemConfig[] } | undefined
)?.documents ?? [
  { id: "aadhaarCard", label: "Aadhaar Card", required: true },
  { id: "panCard", label: "Pan Card", required: true },
  { id: "drivingLicense", label: "Driving License", required: false },
];

export function useFlowNavigation() {
  const route = useRoute();
  const navigation =
    useNavigation<StackNavigationProp<OnboardingStackParamList>>();
  const { steps, flowContext, patchFlowContext } = useActiveFlow();

  /**
   * Navigate to the next non-skipped step in the active flow.
   * Optionally patch flow conditions atomically (e.g. set is_family_kyc) before
   * the skip evaluation so the SAME call both updates state and routes correctly.
   */
  const goToNextStep = useCallback(
    (contextPatch?: Partial<FlowConditionContext>) => {
      const mergedCtx = contextPatch
        ? { ...flowContext, ...contextPatch }
        : flowContext;
      if (contextPatch) patchFlowContext(contextPatch);

      const currentIdx = steps.findIndex((s) => s.id === route.name);
      for (let i = currentIdx + 1; i < steps.length; i++) {
        const step = steps[i];
        if (!evaluateSkipIf(step.skip_if, mergedCtx)) {
          navigation.navigate(step.id as never);
          return;
        }
      }
    },
    [route.name, steps, flowContext, patchFlowContext, navigation],
  );

  /**
   * Returns the typed screen config for the current step, or null if not set.
   * Cast: getStepConfig<"PersonalDetails">() → PersonalDetailsConfig | null
   */
  const getStepConfig = useCallback(<T extends keyof ScreenConfigs>():
    | ScreenConfigs[T]
    | null => {
    const step = steps.find((s) => s.id === route.name);
    if (!step?.config) return null;
    return step.config as ScreenConfigs[T];
  }, [route.name, steps]);

  /** True if the active flow includes AddressDetails (i.e. LAGGAN type). */
  const isLagganFlow = useCallback((): boolean => {
    return steps.some((s) => s.id === "AddressDetails");
  }, [steps]);

  /**
   * True if a later step in the active flow (respecting skip_if against the
   * current/patched flow context) owns the /submit-kyc call. Collection screens
   * use this to decide whether THEY are the terminal step that must submit.
   *
   * In V1 a downstream screen always submits (DocumentOwnership for normal-self,
   * YourDocuments for family, AddressDetails for laggan). V2 drops
   * DocumentOwnership + YourDocuments, so for the NORMAL flow the terminal
   * collection step becomes Documents itself — this returns false there, and
   * Documents fires the submit. Config-driven so it self-adjusts across
   * V1/V2 and NORMAL/LAGGAN without hardcoding versions.
   */
  const willDownstreamStepSubmitKyc = useCallback(
    (contextPatch?: Partial<FlowConditionContext>): boolean => {
      const mergedCtx = contextPatch
        ? { ...flowContext, ...contextPatch }
        : flowContext;
      const currentIdx = steps.findIndex((s) => s.id === route.name);
      for (let i = currentIdx + 1; i < steps.length; i++) {
        const step = steps[i];
        if (evaluateSkipIf(step.skip_if, mergedCtx)) continue;
        if (SUBMIT_KYC_STEP_IDS.has(step.id)) return true;
      }
      return false;
    },
    [route.name, steps, flowContext],
  );

  /**
   * Returns the backend-driven Documents step config (which docs are required).
   * Falls back to the default config if the backend didn't send one.
   */
  const getDocumentsConfig = useCallback((): DocumentItemConfig[] => {
    const step = steps.find((s) => s.id === "Documents");
    return (
      (step?.config as { documents?: DocumentItemConfig[] } | undefined)
        ?.documents ?? FALLBACK_DOCUMENT_CONFIGS
    );
  }, [steps]);

  return {
    goToNextStep,
    getStepConfig,
    isLagganFlow,
    getDocumentsConfig,
    willDownstreamStepSubmitKyc,
  };
}
